import { useEffect, useMemo, useRef, useState } from 'react';
import PreferEarlierTick from '@/components/booking/PreferEarlierTick';
import DepositEmailField, { depositEmailFor } from '@/components/deposits/DepositEmailField';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { useBrainStore } from '@/state/brainStore';
import { activeDeclineFlagForCustomer } from '@/domain/noDepositDeclineFlow';
import { useUIStore } from '@/state/uiStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { generateDepositLink, createAndPayAtDesk } from '@/domain/depositFlow';
import { intentFailureCopy, noDepositHoldIntent } from '@/api/intents';
import { depositLinkMessage, depositLinkRouteNote } from '@/domain/depositLinkSend';
import { deliverDepositMessage, depositMessageRoute } from '@/domain/depositMessageDelivery';
import {
  requiresNoDepositApproval,
  requestNoDepositOverride,
  waiveDepositDirectly,
} from '@/domain/approvals';
import { canApproveNoDepositOverride, canTakeDepositPayment } from '@/domain/permissions';
import { onBookingConfirmed } from '@/domain/bookingConfirmationFlow';
import { generateInstalments, suggestInstalmentAud, scheduleOverrunsAppointment } from '@/domain/topupPlan';
import { scheduleLabel } from '@atink/domain';
import { createTopupPlan, MIN_INSTALMENT_AUD } from '@/domain/topupBuilderFlow';
import { tierDurationMin, clampEndIso, holdExpiryHours, hourToIso } from '@/domain/depositWindow';
import { recommendChannel } from '@/domain/channelRecommendation';
import { depositForSession, priceForArtist, sessionLengthForDuration } from '@/domain/pricing';
import { customerName, aud, formatAud, simTimeShort, simTimeFull, formatBodyPart } from '@/utils/format';
import { customerDepositUrl } from '@/utils/customerLinks';
import type {
  Booking,
  ConversationEntry,
  CustomerRecord,
  DepositChannel,
  SessionLength,
  TopupFrequency,
} from '@/domain/types';
import ArtistDaySchedule from '@/components/queue/ArtistDaySchedule';
import { X, Send, Sparkles, MessageSquare, AtSign, Mail, Minus, Plus, Lock, Banknote, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import { currentActorId } from '@/domain/actor';
import { newPieceFromBrief, newPieceIdFor, newPieceRecord } from '@/domain/newPieceFlow';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { windowNowIso } from '@/domain/messengerWindow';
import DoubleDayWarning from '@/components/DoubleDayWarning';

type LinkChannel = 'sms' | 'meta-dm' | 'ig-dm' | 'email';

// Diagram 02's deposit methods, presented as one peer row where the pay method
// is chosen (tv-6qv.13). On a call, card over the phone is the only take-now
// method (card-in-shop + cash fold into the "Pay at Studio" hold). When the client
// is STANDING IN THE SHOP (a walk-in, tv-2na.8.2) the desk takes card or cash now.
// Mapped onto the existing Deposit.method + DepositChannel unions.
type TakeNowMethodKey = 'card-phone' | 'card-shop' | 'cash';
const TAKE_NOW_METHODS: Array<{
  key: TakeNowMethodKey;
  label: string;
  method: 'eftpos' | 'cash';
  channel: DepositChannel;
}> = [
  { key: 'card-phone', label: 'Card over phone (EFTPOS)', method: 'eftpos', channel: 'card' },
  { key: 'card-shop', label: 'Card in shop (EFTPOS)', method: 'eftpos', channel: 'card' },
  { key: 'cash', label: 'Cash', method: 'cash', channel: 'cash' },
];
type PayMethodKey = 'link' | 'pay-at-studio' | TakeNowMethodKey;
const isTakeNowKey = (k: PayMethodKey): k is TakeNowMethodKey =>
  TAKE_NOW_METHODS.some((m) => m.key === k);

// The unified "how are they paying" row (tv-6qv.13): peer methods, each driving
// both its own pill and (via payMethod) the footer's primary action.
const PAY_METHOD_OPTIONS: Array<{ key: PayMethodKey; label: string; Icon: typeof Send }> = [
  { key: 'link', label: 'Send deposit link', Icon: Send },
  { key: 'card-phone', label: 'Card over phone', Icon: Banknote },
  { key: 'pay-at-studio', label: 'Pay at Studio', Icon: Lock },
];
const IN_SHOP_PAY_METHOD_OPTIONS: Array<{ key: PayMethodKey; label: string; Icon: typeof Send }> = [
  { key: 'card-shop', label: 'Card in shop', Icon: Banknote },
  { key: 'cash', label: 'Cash', Icon: Banknote },
  { key: 'link', label: 'Send deposit link', Icon: Send },
];

// Amounts come from pricing.SESSION_DEPOSIT_AUD (single source of truth) - only
// the display copy lives here.
const SESSION_OPTIONS: Array<{
  value: SessionLength;
  label: string;
  description: string;
}> = [
  { value: 'small', label: 'Small', description: '90 min · single sitting' },
  { value: 'half-day', label: 'Half day', description: '4 hours · half-sleeve' },
  { value: 'full-day', label: 'Full day', description: '8 hours · sleeve / large piece' },
];

const CHANNEL_OPTIONS: Array<{
  value: LinkChannel;
  label: string;
  Icon: typeof MessageSquare;
}> = [
  { value: 'sms', label: 'SMS', Icon: MessageSquare },
  { value: 'meta-dm', label: 'Messenger', Icon: MessageSquare },
  { value: 'ig-dm', label: 'Instagram DM', Icon: AtSign },
  { value: 'email', label: 'Email', Icon: Mail },
];

interface Props {
  open: boolean;
  customer: CustomerRecord;
  onClose: () => void;
  /**
   * Slot discussed + pinned on the call. When present, the window opens in
   * PRE-FILLED mode: a wide two-column layout with booking details + an
   * adjustable session window on the left and the deposit + send on the right.
   * The tier is preselected from the slot length; the end + length are
   * adjustable (start stays locked). Absent opens FROM-SCRATCH: pick an artist,
   * a date and a free window before a deposit can be sent.
   */
  discussedSlot?: { artistId: string; startIso: string; endIso: string };
  /** Seeds the piece-name input (falls back to the design-intent notes). */
  initialPieceName?: string;
  /**
   * The client is in the shop (a walk-in at the desk, tv-2na.8.2): the take-now
   * methods are card in shop + cash, and the default is to take it now.
   */
  inShop?: boolean;
  /**
   * `customer` is a DRAFT not yet on file (a new walk-in): it is created inside
   * the booking's create-and-pay / hold-slot intent, never PUT ahead of it.
   */
  newCustomer?: boolean;
  /**
   * W5b Q1 "Pick a new date" (a resend whose slot passed / was taken): seed the
   * from-scratch picker with the old link's artist, session tier and amount.
   * Absent = the usual defaults. `heading` replaces "Send deposit link".
   */
  initialArtistId?: string;
  initialSession?: SessionLength;
  initialDepositAud?: number;
  heading?: string;
  /** deposits-10: the expired link this one replaces ("Pick a new date"); closed once the new one lands. */
  resendOfDepositId?: string;
}

function recommendSession(customer: CustomerRecord): SessionLength {
  if (customer.designIntent?.size) return customer.designIntent.size;
  const hours = customer.designIntent?.estimatedHours ?? 0;
  if (hours >= 6) return 'full-day';
  if (hours >= 3) return 'half-day';
  return 'small';
}

function castChannel(c: string): LinkChannel {
  if (c === 'sms' || c === 'meta-dm' || c === 'ig-dm' || c === 'email') return c;
  return 'sms';
}

// Tier size order, for comparing the selected tier against the slot's natural
// bracket. A tier one bracket LARGER than the slot can hold is the overshoot.
const TIER_INDEX: SessionLength[] = ['small', 'half-day', 'full-day', 'multi-session'];

/** A fresh deposit id, in the same `dep-<ms>-<n>` format a new piece uses. */
const mintDepositId = () => `dep-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

/** Duration of a slot in minutes. */
function slotMinutes(startIso: string, endIso: string): number {
  return Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000);
}

/** Compact "3h" / "3h 15m" duration label. */
function durationLabel(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// Hosts (My Queue, the slot pane, the composer) keep this modal permanently
// mounted and only toggle `open`. Seeding the slot/tier state once at first
// mount would therefore capture a still-empty slot and go stale the instant one
// is pinned after mount - which showed up as a blank Date and a missing session
// window in the grounded view. Remounting the body whenever the discussed slot
// changes re-seeds that state from the current props.
export default function DepositLinkModal(props: Props) {
  const slot = props.discussedSlot;
  // The brief size is part of the seed (it drives the initial tier below), so a
  // size picked in the brief AFTER this modal first mounted must re-seed it too -
  // not just a slot change. Fold it into the remount key.
  const briefSize = props.customer.designIntent?.size ?? 'auto';
  // tv-2mwa: every open, and every customer, starts fresh. Hosts keep the modal
  // mounted, so the last call's date and top-up setting used to carry into the
  // next call. Count the opens (closed -> open) and key on them + the customer.
  const [openCount, setOpenCount] = useState(props.open ? 1 : 0);
  const [wasOpen, setWasOpen] = useState(props.open);
  if (props.open !== wasOpen) {
    setWasOpen(props.open);
    if (props.open) setOpenCount((n) => n + 1);
  }
  return (
    <DepositLinkModalInner
      key={`${slot ? `${slot.artistId}-${slot.startIso}-${slot.endIso}` : 'scratch'}-${briefSize}-${props.customer.id}-${openCount}`}
      {...props}
    />
  );
}

function DepositLinkModalInner({
  open,
  customer,
  onClose,
  discussedSlot,
  initialPieceName,
  inShop = false,
  newCustomer = false,
  initialArtistId,
  initialSession: sessionOverride,
  initialDepositAud,
  heading,
  resendOfDepositId,
}: Props) {
  const upsertBooking = useBrainStore((s) => s.upsertBooking);
  const upsertCustomer = useBrainStore((s) => s.upsertCustomer);
  const appendActivity = useBrainStore((s) => s.appendActivity);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const artistsById = useBrainStore((s) => s.artists);
  const piecesById = useBrainStore((s) => s.pieces);
  const persona = useSessionStore((s) => s.persona);

  const studioArtists = useMemo(
    () =>
      Object.values(artistsById)
        .filter((a) => a.studioId === customer.primaryStudioId && !a.archived)
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [artistsById, customer.primaryStudioId],
  );

  // The brief's explicit size selection is authoritative: a size picked in the
  // brief seeds the tier here even when a slot is pinned. With no brief size,
  // pre-filled mode preselects the tier from the picked slot's length and from
  // scratch falls back to the customer's design intent.
  const initialSession = sessionOverride
    ? sessionOverride
    : customer.designIntent?.size
    ? customer.designIntent.size
    : discussedSlot
      ? sessionLengthForDuration(slotMinutes(discussedSlot.startIso, discussedSlot.endIso))
      : recommendSession(customer);
  const channelRec = useMemo(() => recommendChannel(customer), [customer]);
  const recommendedChannel = castChannel(channelRec.channel);

  const [session, setSession] = useState<SessionLength>(initialSession);
  const [channel, setChannel] = useState<LinkChannel>(recommendedChannel);
  // Minted per open (tv-isee): the preview shows this deposit's real URL, and the link path and a new piece reuse it.
  // Hosts keep this modal mounted, so it re-mints on each open and after each send; a reused id would overwrite the last deposit.
  const [mintedDepositId, setMintedDepositId] = useState(mintDepositId);
  const wasOpenRef = useRef(open);
  useEffect(() => {
    if (open && !wasOpenRef.current) setMintedDepositId(mintDepositId());
    wasOpenRef.current = open;
  }, [open]);
  // B7 (8xat D08-09): "Happy to come in earlier if a slot opens", onto the booking.
  const [preferEarlier, setPreferEarlier] = useState(false);
  const [pieceName, setPieceName] = useState(
    initialPieceName ?? customer.designIntent?.notes ?? '',
  );

  // Slot state. In pre-filled mode the block is seeded from the discussed slot;
  // from-scratch mode fills it in when the agent picks a free window.
  const [artistId, setArtistId] = useState(
    discussedSlot?.artistId ?? initialArtistId ?? studioArtists[0]?.id ?? '',
  );
  const [blockStartIso, setBlockStartIso] = useState<string | null>(
    discussedSlot?.startIso ?? null,
  );
  const [blockEndIso, setBlockEndIso] = useState<string | null>(discussedSlot?.endIso ?? null);
  const [endIso, setEndIso] = useState<string | null>(null);
  const [dateValue, setDateValue] = useState(() => format(new Date(clockIso), 'yyyy-MM-dd'));

  // Rate + deposit overrides. Empty string = follow the auto/standard value.
  const [dayRateInput, setDayRateInput] = useState('');
  // A prefilled amount that differs from the tier standard opens as a custom deposit.
  const prefillCustomDeposit =
    initialDepositAud != null &&
    initialDepositAud !== depositForSession(initialSession, artistsById[discussedSlot?.artistId ?? initialArtistId ?? ''])
      ? String(initialDepositAud)
      : '';
  const [depositInput, setDepositInput] = useState(prefillCustomDeposit);
  // Gates the custom rate/deposit inputs behind an explicit toggle. Off by
  // default so the tier presets are the primary control; when on, the presets
  // are greyed out + disabled since the agent is setting their own numbers.
  const [useCustomRate, setUseCustomRate] = useState(prefillCustomDeposit !== '');
  // Mandatory reason when the deposit is $0 (the no-deposit path). Approvals
  // rejects a blank note, so this gates the request/waive action.
  const [overrideReason, setOverrideReason] = useState('');
  // Unified "how are they paying" choice (tv-6qv.13): the three peer methods
  // drive both the method row's active pill and the footer's single primary
  // action/label. Defaults to the Stripe link so pre-existing send behaviour is
  // preserved unless the agent switches methods.
  const [payMethod, setPayMethod] = useState<PayMethodKey>(inShop ? 'card-shop' : 'link');
  const payMethodOptions = inShop ? IN_SHOP_PAY_METHOD_OPTIONS : PAY_METHOD_OPTIONS;
  const isTakeNow = isTakeNowKey(payMethod);
  // Take-now create-and-pay in flight (latches the primary), the ids to re-send on
  // a retry (idempotent), and the last failure's copy shown inline (tv-2na.8).
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  // `key` pins the ids to the slot + amount they were minted for: a retry only
  // re-sends them for the SAME booking (a changed slot is a different booking).
  const pendingIdsRef = useRef<{ key: string; bookingId: string; depositId?: string } | null>(null);
  const [intentError, setIntentError] = useState<ReturnType<typeof intentFailureCopy> | null>(null);

  // Pay-over-time plan builder (tv-1a9). Off by default; when on, the deposit is
  // collected as instalments and the first one holds the date. The per-instalment
  // amount seeds from a sensible quarter-split of the tier's standard deposit,
  // clamped to the $50 floor.
  const [planEnabled, setPlanEnabled] = useState(false);
  // tv-6qv.20: a plan goes out only as a deposit link (the customer pays each
  // instalment on their page). Card now and pay-at-studio take the full deposit,
  // so the builder isn't offered for them and a plan built earlier is never used.
  const planOffered = payMethod === 'link';
  const planOn = planEnabled && planOffered;
  const [planStartIso, setPlanStartIso] = useState(() => format(new Date(clockIso), 'yyyy-MM-dd'));
  const [planFrequency, setPlanFrequency] = useState<TopupFrequency>('fortnightly');
  const [planInstalmentAud, setPlanInstalmentAud] = useState(() =>
    Math.max(MIN_INSTALMENT_AUD, Math.round(depositForSession(initialSession, artistsById[artistId]) / 4)),
  );
  // Tracks whether the agent has hand-edited the per-instalment amount, so the
  // suggestion effect below only seeds it once (on toggling the plan on) and
  // never clobbers a deliberate manual value.
  const [planTouched, setPlanTouched] = useState(false);

  const grounded = !!discussedSlot;
  // Messengers (IG/FB DM inbox) can only ever send a payment link - the customer
  // pays it and supplies their own number customer-side. Every other persona
  // (agents on a call, desk, management) also has the in-person methods. A null
  // persona (tests / no session) is treated as a full-access agent.
  const canTakePayment = persona ? canTakeDepositPayment(persona) : true;
  const spec = SESSION_OPTIONS.find((s) => s.value === session) ?? SESSION_OPTIONS[1]!;
  const channelMeta = CHANNEL_OPTIONS.find((c) => c.value === channel) ?? CHANNEL_OPTIONS[0]!;

  const tierMin = tierDurationMin(session);
  const startIso = blockStartIso;
  const defaultEndIso = startIso
    ? new Date(new Date(startIso).getTime() + tierMin * 60000).toISOString()
    : null;
  const effectiveEndIso =
    startIso && blockEndIso ? clampEndIso(startIso, endIso ?? defaultEndIso!, blockEndIso) : null;
  // The window always clamps to the free block, so a shorter block is not an
  // error - the hold just runs to the block end. Overshoot warns only when the
  // agent picks a tier a full bracket LARGER than the slot can naturally hold
  // (e.g. a full-day deposit on a half-day window).
  const blockMin =
    startIso && blockEndIso ? slotMinutes(startIso, blockEndIso) : tierMin;
  const isOvershoot =
    !!(startIso && blockEndIso) &&
    TIER_INDEX.indexOf(session) > TIER_INDEX.indexOf(sessionLengthForDuration(blockMin));
  const hasSlot = !!(startIso && effectiveEndIso);
  const expiryHours = startIso ? holdExpiryHours(startIso, clockIso) : 48;

  // Held length in minutes (the ghost booking's duration). Falls back to the
  // tier length before a slot is grounded.
  const lengthMin =
    startIso && effectiveEndIso ? slotMinutes(startIso, effectiveEndIso) : tierMin;

  // Day the from-scratch schedule + picked window resolve against (local day).
  const dayIso = useMemo(() => new Date(`${dateValue}T00:00:00`).toISOString(), [dateValue]);

  const selectedArtist = artistsById[artistId];
  const selectedArtistName = selectedArtist?.displayName ?? artistId;

  // Day rate = the artist's RATE-CARD TIER for the picked session (DECIDED 25 Sep,
  // Q8 + Q9, tv-2na.8.22 / D13 — not hours x hourly, which priced a half day at
  // $880 against the $800 card); the agent can override by typing. Deposit
  // follows the tier standard until overridden. The two are independent.
  const autoDayRate = selectedArtist ? priceForArtist(selectedArtist, session) : 0;
  const effectiveDayRate = dayRateInput === '' ? autoDayRate : Math.max(0, Math.round(Number(dayRateInput) || 0));
  const standardDeposit = depositForSession(session, selectedArtist);
  const effectiveDeposit =
    depositInput === '' ? standardDeposit : Math.max(0, Math.round(Number(depositInput) || 0));

  // Plan schedule, recomputed each render so the preview tracks the inputs. The
  // date input is a local calendar day; anchor it at local midnight so the
  // rendered schedule shows the day the agent picked, not a UTC shift of it.
  const planStartIsoFull = new Date(`${planStartIso}T00:00:00`).toISOString();
  const planInvalid = planInstalmentAud < MIN_INSTALMENT_AUD;
  const planSchedule =
    effectiveDeposit > 0 && !planInvalid
      ? generateInstalments({
          startIso: planStartIsoFull,
          frequency: planFrequency,
          instalmentAud: planInstalmentAud,
          totalAud: effectiveDeposit,
        })
      : [];

  // The appointment the plan must clear by: the discussed slot's start when
  // grounded (hasSlot gates Send, so it is present by the time a plan is sent),
  // falling back to the plan's own start date from-scratch.
  const appointmentIso = startIso ?? planStartIsoFull;
  const suggestedInstalmentAud = useMemo(
    () =>
      suggestInstalmentAud({
        totalAud: effectiveDeposit,
        firstIso: planStartIsoFull,
        appointmentIso,
        frequency: planFrequency,
      }),
    [effectiveDeposit, planStartIsoFull, appointmentIso, planFrequency],
  );
  // B4 (75my): every instalment is paid by the appointment — a plan that runs past
  // it can't be sent (the server refuses it too); raise the amount or shorten it.
  const planOverruns = planOn && !planInvalid && scheduleOverrunsAppointment(planSchedule, appointmentIso);

  // Seeds the per-instalment amount from the suggestion the moment the plan is
  // switched on, but only while the agent hasn't hand-edited it - an explicit
  // manual value is never overwritten.
  useEffect(() => {
    if (planEnabled && !planTouched) setPlanInstalmentAud(suggestedInstalmentAud);
  }, [planEnabled, planTouched, suggestedInstalmentAud]);

  const styleLabel = customer.preferredStyle?.replace('-', ' ');
  const placement = customer.designIntent?.placement;
  // Prefer the brief's explicit colour; fall back to deriving from the design type.
  const colour: 'colour' | 'black-and-grey' | undefined =
    customer.designIntent?.colour ??
    (customer.designIntent?.type
      ? /colou?r/.test(customer.designIntent.type)
        ? 'colour'
        : 'black-and-grey'
      : undefined);
  const collabArtistIds = customer.designIntent?.collabArtistIds ?? [];
  const briefPieceId =
    customer.designIntent?.pieceMode === 'continue' ? customer.designIntent?.continuePieceId : undefined;
  const briefRefs = customer.designIntent?.referencePhotos ?? [];
  // A NEW multi-session piece in the brief (tv-2na.11.22, D27): it rides the
  // hold-slot / create-and-pay intent as `newPiece`, created as piece-<depositId>.
  const briefNewPiece = briefPieceId
    ? undefined
    : newPieceFromBrief(customer.designIntent, {
        pieceName: pieceName.trim(),
        style: customer.preferredStyle,
        placement: customer.designIntent?.placement,
      });
  const bodyPart = customer.designIntent?.bodyPart;
  const bodyPartSide = customer.designIntent?.bodyPartSide;
  const bodyPartQualifier = customer.designIntent?.bodyPartQualifier;

  // Read-only brief summaries for the booking-details card - these mirror the
  // segmented brief in the left rail (piece / style & artist / placement / look
  // / references) so the deposit confirms the same thing the agent just built.
  const di = customer.designIntent;
  const pieceMode = di?.pieceMode;
  const continuedPiece = di?.continuePieceId ? piecesById[di.continuePieceId] : undefined;
  const pieceSummary =
    pieceMode === 'continue'
      ? continuedPiece
        ? `${continuedPiece.title || continuedPiece.style || continuedPiece.designType.replace(/-/g, ' ')} · continuing`
        : 'Continuing a piece'
      : di?.newPieceTitle
        ? pieceMode === 'one-off'
          ? `${di.newPieceTitle} · one-off`
          : `${di.newPieceTitle}${di.estimatedSessions ? ` · ${di.estimatedSessions} sessions` : ''}`
        : undefined;
  // One formatter for the whole app (was a second, differently-ordered one here).
  // Falls back to the free-text placement, which is what the picker's "Other"
  // pill writes, so an unlisted body part still reads correctly on the brief.
  const placementLabel = formatBodyPart({ bodyPart, bodyPartSide, bodyPartQualifier, placement });
  const sizeLabel =
    di?.size === 'small'
      ? 'Small'
      : di?.size === 'half-day'
        ? 'Half day'
        : di?.size === 'full-day'
          ? 'Full day'
          : di?.size === 'multi-session'
            ? 'Multi-session'
            : undefined;
  const collabNames = collabArtistIds.map((id) => artistsById[id]?.displayName ?? id);

  // Picking a tier is the primary control: it snaps the window back to the
  // tier's default length and resets the rate/deposit overrides, so the auto
  // values track the tier until the agent adjusts them again.
  const changeSession = (s: SessionLength) => {
    setSession(s);
    setEndIso(null);
    setDayRateInput('');
    setDepositInput('');
  };

  const stepEnd = (deltaMin: number) => {
    if (!startIso || !blockEndIso) return;
    const base = endIso ?? defaultEndIso!;
    setEndIso(
      clampEndIso(
        startIso,
        new Date(new Date(base).getTime() + deltaMin * 60000).toISOString(),
        blockEndIso,
      ),
    );
  };

  const onPickWindow = (w: { startHour: number; endHour: number }) => {
    setBlockStartIso(hourToIso(dayIso, w.startHour));
    setBlockEndIso(hourToIso(dayIso, w.endHour));
    setEndIso(null);
  };

  // End stepper bounds, for accessible disabled states. Shared by the end and
  // length steppers (length just steps the end).
  const canDecEnd = !!(
    effectiveEndIso &&
    startIso &&
    new Date(effectiveEndIso).getTime() > new Date(startIso).getTime() + 15 * 60000
  );
  const canIncEnd = !!(
    effectiveEndIso &&
    blockEndIso &&
    new Date(effectiveEndIso).getTime() < new Date(blockEndIso).getTime()
  );

  // The slot is ready to hang a booking off once a window is picked and the tier
  // does not overshoot it. Shared by every send/take/no-deposit action.
  const slotReady = hasSlot && !isOvershoot;
  // A $0 deposit is the no-deposit path: privileged personas waive it directly
  // (note-forced), everyone else raises an approval request. Both replace the
  // Stripe-link send. Mirrors NewBookingModal's controlled no-deposit routing.
  const needsApproval = requiresNoDepositApproval(effectiveDeposit);
  const isPrivileged = persona ? canApproveNoDepositOverride(persona) : false;
  // B3 (75mp): after an Ops decline the no-money route is closed for this customer
  // (no second trip through Ops) until a deposit is paid. Ops may still waive.
  const declinedNoDeposit = useBrainStore((s) =>
    !isPrivileged && s.noDepositDeclineFlags[`ndf-${customer.id}`] ? !!activeDeclineFlagForCustomer(customer.id) : false,
  );
  const canSubmitNoDeposit = slotReady && overrideReason.trim().length > 0 && !declinedNoDeposit;
  // A real phone is mandatory before a payment can complete (Diagram 02). Agents
  // take payment while on a call, so the number is already on file; take-now
  // reads it from the profile and still blocks when nothing is on file.
  const onFilePhone = (customer.phone ?? '').trim();
  // D05-11 (Anas, 6 Oct): an email is mandatory when the deposit is taken now —
  // the one on file, else typed here (a link send needs none: the pay page asks).
  const [takeEmail, setTakeEmail] = useState('');
  const contactEmail = depositEmailFor(customer, takeEmail);
  const canTakeNow = slotReady && effectiveDeposit > 0 && onFilePhone.length > 0 && !!contactEmail;
  // The selected take-now method (card over phone on a call; card/cash in shop).
  const takeNowSpec = TAKE_NOW_METHODS.find((m) => m.key === payMethod) ?? TAKE_NOW_METHODS[0]!;

  // Held/confirmed ghost booking built from the picked slot + brief, so the
  // no-deposit request and take-now payment have a real booking to attach to.
  // Mirrors the held booking generateDepositLink writes, minus the deposit.
  const buildBooking = (id: string, status: Booking['status']): Booking => {
    const trimmedPieceName = pieceName.trim();
    const styleTags = customer.preferredStyle ? [customer.preferredStyle] : [];
    return {
      id,
      customerId: customer.id,
      // The artist's studio (B7): the calendar reads bookings by studio, and the
      // server refuses a booking filed under a studio its artist isn't in.
      studioId: artistsById[artistId]?.studioId ?? customer.primaryStudioId,
      artistId,
      start: startIso!,
      durationMin: lengthMin,
      sessionLength: session,
      status,
      // tv-2mwa: the piece name typed here, else the brief's description, as the hold does.
      designBrief:
        trimmedPieceName ||
        customer.designIntent?.description?.trim() ||
        customer.designIntent?.notes ||
        'Booking from deposit screen.',
      referencePhotos: briefRefs,
      source: 'agent-initiated-chat',
      createdAt: clockIso,
      dayRateAud: effectiveDayRate,
      ...(placement ? { placement } : {}),
      ...(bodyPart ? { bodyPart } : {}),
      ...(bodyPartSide ? { bodyPartSide } : {}),
      ...(bodyPartQualifier ? { bodyPartQualifier } : {}),
      ...(styleTags.length ? { styleTags } : {}),
      ...(colour ? { colour } : {}),
      ...(collabArtistIds.length ? { collabArtistIds } : {}),
      ...(briefPieceId ? { pieceId: briefPieceId } : {}),
      ...(preferEarlier ? { preferBroughtForward: true } : {}),
    };
  };

  // Show the new piece at once: mirrored as a server-authored write (the intent
  // creates it — the bridge never PUTs it).
  const mirrorNewPiece = (depositId: string, bookingId: string) => {
    if (!briefNewPiece || !startIso) return;
    const piece = newPieceRecord(briefNewPiece, depositId, buildBooking(bookingId, 'held'));
    withServerAuthoredWrites([['pieces', piece.id]], () => useBrainStore.getState().upsertPiece(piece));
  };

  // Create the held booking both no-deposit paths hang off (reuses the picked
  // slot, grounded or from-scratch). Returns the booking id, or undefined if the
  // slot is not ready.
  const createHeldBooking = (): string | undefined => {
    if (!slotReady || !startIso) return undefined;
    // A draft walk-in customer must exist before a booking can reference it.
    if (newCustomer) upsertCustomer(customer);
    const id = `bk-${Date.now()}`;
    const booking = buildBooking(id, 'held');
    upsertBooking(booking);
    appendActivity({
      id: `act-bk-${id}`,
      type: 'booking-created',
      customerId: customer.id,
      studioId: booking.studioId,
      artistId,
      body: `Held ${simTimeFull(startIso)} for ${customerName(customer)} with ${selectedArtistName} - no deposit.`,
      timestamp: clockIso,
    });
    return id;
  };

  // $0 path: raise a no-deposit approval (non-privileged) or waive directly
  // (Georgina/Owner). Note is mandatory - approvals refuses a blank one.
  const submitNoDeposit = () => {
    if (!canSubmitNoDeposit) return;
    const note = overrideReason.trim();
    // 7b6n: served, the booking + its request (+ a new walk-in) land in ONE intent.
    if (!isStoreFallbackEnabled()) {
      void submitNoDepositServed(note);
      return;
    }
    const id = createHeldBooking();
    if (!id) return;
    if (isPrivileged) {
      waiveDepositDirectly({ bookingId: id, note });
      pushNotification({
        intensity: 'high',
        title: 'Booking confirmed - no deposit',
        body: 'Deposit waived and logged. Customer pays in full on the day.',
        ttlMs: 4500,
      });
    } else {
      requestNoDepositOverride({ bookingId: id, note });
      pushNotification({
        intensity: 'high',
        title: 'Sent for approval',
        body: 'Slot held. Georgina will approve or reject the no-deposit request.',
        ttlMs: 5000,
      });
    }
    onClose();
  };

  // 7b6n: the $0 booking through the no-deposit-hold intent. Nothing is in the
  // store until the server commits it; then the booking, the request (and a new
  // customer) are mirrored without PUTs and the usual side effects run.
  const submitNoDepositServed = async (note: string) => {
    if (!slotReady || !startIso || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    const bookingId = `bk-${Date.now()}`;
    const requestId = `ovr-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const by = currentActorId() ?? 'system';
    const at = clockIso;
    const held = buildBooking(bookingId, 'held');
    const res = await noDepositHoldIntent({
      booking: held,
      request: {
        id: requestId,
        status: isPrivileged ? 'approved' : 'pending',
        requestedBy: by,
        note,
        createdAt: at,
        ...(isPrivileged ? { resolvedBy: by, resolvedAt: at } : {}),
      },
      ...(newCustomer ? { customer } : {}),
    });
    submittingRef.current = false;
    setSubmitting(false);
    if (!res.ok) {
      const copy = intentFailureCopy(res);
      pushNotification({ intensity: 'critical', title: copy.title, body: copy.body, ttlMs: 8000, customerId: customer.id });
      return;
    }
    withServerAuthoredWrites(
      [
        ...(newCustomer ? ([['customers', customer.id]] as const) : []),
        ['bookings', bookingId],
        ['overrideRequests', requestId],
      ],
      () => {
        if (newCustomer) upsertCustomer(customer);
        upsertBooking(held);
        appendActivity({
          id: `act-bk-${bookingId}`,
          type: 'booking-created',
          customerId: customer.id,
          studioId: held.studioId,
          artistId,
          body: `Held ${simTimeFull(startIso)} for ${customerName(customer)} with ${selectedArtistName} - no deposit.`,
          timestamp: clockIso,
        });
        if (isPrivileged) waiveDepositDirectly({ bookingId, note, by, id: requestId });
        else requestNoDepositOverride({ bookingId, note, requestedBy: by, id: requestId });
      },
    );
    pushNotification(
      isPrivileged
        ? { intensity: 'high', title: 'Booking confirmed - no deposit', body: 'Deposit waived and logged. Customer pays in full on the day.', ttlMs: 4500 }
        : { intensity: 'high', title: 'Sent for approval', body: 'Slot held. Georgina will approve or reject the no-deposit request.', ttlMs: 5000 },
    );
    onClose();
  };

  // Take payment now (card over the phone, or card/cash in the shop): the fresh
  // confirmed booking + its paid deposit (+ a draft walk-in customer) land through
  // ONE create-and-pay intent (tv-2na.8). The booking-created activity, the waiver
  // + confirmation SMS, the success toast and closing the modal run ONLY once the
  // server says ok. slot_taken keeps the modal open to pick another time; a
  // transient failure offers a retry with the SAME ids (idempotent).
  const takePaymentNow = () => {
    if (!canTakeNow || !startIso || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setIntentError(null);
    const displayName = customerName(customer);
    const key = `${artistId}|${startIso}|${lengthMin}|${effectiveDeposit}|${customer.id}`;
    const ids =
      pendingIdsRef.current?.key === key
        ? pendingIdsRef.current
        : {
            key,
            bookingId: `bk-${Date.now()}`,
            // A new piece is keyed on the deposit id, so it is minted here (not in the flow).
            ...(briefNewPiece ? { depositId: `dep-manual-${Date.now()}-${Math.floor(Math.random() * 1000)}` } : {}),
          };
    pendingIdsRef.current = ids;
    const booking = {
      ...buildBooking(ids.bookingId, 'confirmed'),
      ...(briefNewPiece && ids.depositId ? { pieceId: newPieceIdFor(ids.depositId) } : {}),
    };
    const spec = takeNowSpec;
    const release = () => {
      submittingRef.current = false;
      setSubmitting(false);
    };
    void createAndPayAtDesk({
      booking,
      ...(ids.depositId ? { depositId: ids.depositId } : {}),
      amountAud: effectiveDeposit,
      method: spec.method,
      channel: spec.channel,
      by: currentActorId() ?? 'front-desk',
      ...(contactEmail ? { contactEmail } : {}),
      ...(newCustomer ? { customer } : {}),
      ...(briefNewPiece ? { newPiece: briefNewPiece } : {}),
      onConfirmed: ({ bookingId, depositId }) => {
        pendingIdsRef.current = null;
        release();
        mirrorNewPiece(depositId, bookingId);
        appendActivity({
          id: `act-bk-${bookingId}`,
          type: 'booking-created',
          customerId: customer.id,
          studioId: booking.studioId,
          artistId,
          body: `Confirmed ${simTimeFull(startIso)} for ${displayName} with ${selectedArtistName}.`,
          timestamp: clockIso,
        });
        // Booking is confirmed on the spot: the server texts the receipt + waiver (B6).
        onBookingConfirmed(bookingId, { paidOnServer: true });
        pushNotification({
          intensity: 'high',
          title: 'Deposit paid - receipt sent',
          body: `${aud(effectiveDeposit)} by ${spec.label.toLowerCase()} - booking confirmed. Receipt sent, number on file.`,
          ttlMs: 4500,
          customerId: customer.id,
        });
        onClose();
      },
      onFailed: (f) => {
        release();
        const copy = intentFailureCopy(f);
        // Keep the ids only for a retry-able failure; a taken slot / refusal
        // starts fresh (another time is a different booking).
        pendingIdsRef.current = copy.retry ? { key, bookingId: f.bookingId, depositId: f.depositId } : null;
        setIntentError(copy);
        pushNotification({
          intensity: 'critical',
          title: copy.title,
          body: copy.body,
          ttlMs: 8000,
          customerId: customer.id,
        });
      },
    });
  };

  // Shared arg shape for the deposit hold, used by both the link-send and the
  // pay-at-studio hold so the two paths can only diverge AFTER creation
  // (tv-6qv.13, Task 5 DRY follow-up). Previously each call site duplicated
  // this object literal field-for-field - a drift hazard where a new brief
  // field added to one path could silently go missing on the other.
  const buildDepositLinkArgs = (startIso: string, endIso: string) => {
    const trimmedPieceName = pieceName.trim();
    const styleTags = customer.preferredStyle ? [customer.preferredStyle] : [];
    // A new piece is keyed on the deposit id (minted on open).
    return {
      id: mintedDepositId,
      ...(briefNewPiece ? { pieceId: newPieceIdFor(mintedDepositId), newPiece: briefNewPiece } : {}),
      customerId: customer.id,
      studioId: customer.primaryStudioId,
      artistId,
      amountAud: effectiveDeposit,
      channel,
      sessionLength: session,
      source: 'agent-initiated-chat' as const,
      discussedSlot: { artistId, startIso, endIso },
      dayRateAud: effectiveDayRate,
      ...(preferEarlier ? { preferBroughtForward: true } : {}),
      ...(trimmedPieceName ? { pieceName: trimmedPieceName } : {}),
      // tv-2mwa: the brief's description rides the hold when no piece name was typed.
      ...(!trimmedPieceName && customer.designIntent?.description?.trim()
        ? { designBrief: customer.designIntent.description.trim() }
        : {}),
      ...(placement ? { placement } : {}),
      ...(bodyPart ? { bodyPart } : {}),
      ...(bodyPartSide ? { bodyPartSide } : {}),
      ...(bodyPartQualifier ? { bodyPartQualifier } : {}),
      ...(styleTags.length ? { styleTags } : {}),
      ...(colour ? { colour } : {}),
      ...(collabArtistIds.length ? { collabArtistIds } : {}),
      ...(briefPieceId ? { pieceId: briefPieceId } : {}),
      ...(briefRefs.length ? { referencePhotos: briefRefs } : {}),
      // A draft walk-in customer is created inside the hold-slot intent.
      ...(newCustomer ? { customer } : {}),
      ...(resendOfDepositId ? { resendOfDepositId } : {}),
    };
  };

  // Where the link goes (tv-isee), for the route note. Wall time: Meta's windows are real time.
  const wallNowIso = new Date().toISOString();
  const linkRoute = depositMessageRoute({ customer, channel, nowIso: wallNowIso, localOnly: (planOn && !planInvalid) || payMethod !== 'link' });
  const routeNote = depositLinkRouteNote({ route: linkRoute, customer, fallbackName: customerName(customer), nowIso: wallNowIso });
  // `amount` defaults to the unmasked formatter: this is the text the customer receives.
  // The preview passes `aud` so a persona with financials 'none' sees $••••; the
  // customer still receives the real amount.
  const linkMessage = (depositId: string, amount: (n: number) => string = formatAud) =>
    depositLinkMessage({ firstName: customer.firstName, sessionLabel: spec.label, amountAud: amount(effectiveDeposit), expiryHours, url: customerDepositUrl(depositId) });

  const onSend = () => {
    if (!hasSlot || isOvershoot || !startIso || !effectiveEndIso) return;
    // A $0 deposit never sends a link - it routes through the no-deposit path.
    if (needsApproval) return;
    const displayName = customerName(customer);
    const linkArgs = buildDepositLinkArgs(startIso, effectiveEndIso);
    const dep = generateDepositLink(linkArgs);
    if (linkArgs.id) mirrorNewPiece(linkArgs.id, `bk-${linkArgs.id}`);
    const localNote: ConversationEntry = {
      id: `convo-dep-${dep.id}`,
      customerId: customer.id,
      channel,
      direction: 'out',
      body: `Sent ${displayName} a ${aud(effectiveDeposit)} deposit link for a ${spec.label.toLowerCase()} session: ${customerDepositUrl(dep.id)}`,
      // A real HookChat thread runs on wall time (tv-isee); the sim clock would sort this above the real DMs.
      timestamp: windowNowIso(customer, clockIso),
      authorRole: 'agent',
      authorName: 'You',
      depositId: dep.id,
    };
    const route = deliverDepositMessage({ customer, channel, text: linkMessage(dep.id), depositId: dep.id, localNote, nowIso: new Date().toISOString(), localOnly: planOn && !planInvalid });
    if (route === 'local') {
      pushNotification({
        intensity: 'info',
        title: planOn ? 'Deposit plan sent' : 'Deposit link sent',
        body: `${aud(effectiveDeposit)} on ${channelMeta.label}. Watch the conversation for the lifecycle.`,
        ttlMs: 4000,
      });
    }
    // Plan branch: build the pay-over-time plan against the fresh deposit and let
    // payment arrive through the instalments / tap-to-pay page - NOT the single
    // full-amount webhook. Toggle OFF keeps the original single-payment lifecycle.
    if (planOn && !planInvalid) {
      createTopupPlan({
        depositId: dep.id,
        startIso: planStartIsoFull,
        frequency: planFrequency,
        instalmentAud: planInstalmentAud,
        at: clockIso,
      });
    }
    // No demo auto-advance: a sent deposit link stays "sent" until the customer
    // views/pays it (via the Customer Simulator or the deposit card). This keeps
    // staging a living app where deposits don't pay themselves.
    setMintedDepositId(mintDepositId());
    onClose();
  };

  const sendDisabled = !hasSlot || isOvershoot || (planOn && (planInvalid || planOverruns));

  // Unified footer primary (tv-6qv.13): the method row's choice drives a single
  // action/label/disabled state instead of a separate take-now button living
  // alongside the send button. "Pay at Studio" is a pay-on-arrival hold: the
  // agent (on a call) can't take in-person money, so the slot is held with a
  // PENDING deposit via generateDepositLink's holdOnly path — a first-class
  // in-person hold: NO link is sent, NO online payment lifecycle is scheduled,
  // and it is exempt from the 24h link-expiry reconcile. The Floor settles
  // card/cash at the desk when the client arrives, often weeks out (tv-6qv.19).
  const holdPayAtStudio = () => {
    if (!slotReady || !startIso || !effectiveEndIso) return;
    // A $0 deposit routes through the no-deposit approval path instead.
    if (needsApproval) return;
    // Pay-at-studio takes no phone now (the Floor collects it at the desk).
    const displayName = customerName(customer);
    // holdOnly: generateDepositLink logs the single truthful "Held … pending
    // deposit." activity and stamps the deposit payAtStudio; we must NOT add a
    // second activity here (double-log) nor schedule any viewed/paid timers.
    const holdArgs = buildDepositLinkArgs(startIso, effectiveEndIso);
    generateDepositLink({ ...holdArgs, holdOnly: true });
    if (holdArgs.id) mirrorNewPiece(holdArgs.id, `bk-${holdArgs.id}`);
    setMintedDepositId(mintDepositId());
    pushNotification({
      intensity: 'info',
      title: 'Slot held — pay at studio',
      body: `${displayName} will pay ${aud(effectiveDeposit)} at the desk. Slot held pending payment.`,
      ttlMs: 4000,
    });
    onClose();
  };
  const primaryLabel =
    isTakeNow
      ? submitting
        ? 'Saving…'
        : intentError?.retry
          ? `Retry · take ${aud(effectiveDeposit)} now`
          : `Take ${aud(effectiveDeposit)} now`
      : payMethod === 'pay-at-studio'
        ? 'Hold slot · pay at studio'
        : planOn && !planInvalid
          ? `Send plan · ${scheduleLabel(planSchedule)} · hold slot`
          : `Send ${aud(effectiveDeposit)} link · hold slot`;
  const onPrimary = () => {
    if (isTakeNow) return takePaymentNow();
    if (payMethod === 'pay-at-studio') return holdPayAtStudio();
    return onSend();
  };
  const primaryDisabled =
    isTakeNow ? !canTakeNow || submitting : payMethod === 'pay-at-studio' ? !slotReady : sendDisabled;
  // Icon follows the method row's icon (Banknote / Lock / Send) so the footer
  // primary doesn't look mismatched against the pill the agent just picked.
  const PrimaryIcon = isTakeNow ? Banknote : payMethod === 'pay-at-studio' ? Lock : Send;
  // Disabled-reason hover copy, ported from the old standalone take-now button
  // (card-phone) plus sensible equivalents for pay-at-studio and the link send.
  const primaryTitle =
    isTakeNow
      ? !slotReady
        ? 'Pick a slot that fits the tier first'
        : onFilePhone.length === 0
          ? 'No phone on file. Add one with Edit details on their record to take payment'
          : !contactEmail
            ? "Add the client's email to take payment"
            : `Take ${aud(effectiveDeposit)} now`
      : payMethod === 'pay-at-studio'
        ? !slotReady
          ? 'Pick a slot to hold'
          : 'Hold the slot — pay at the studio (card or cash at the desk)'
        : isOvershoot
          ? 'Pick a tier that fits the slot'
          : !hasSlot
            ? 'Pick a slot first'
            : planOn && planInvalid
              ? `Each instalment must be at least ${aud(MIN_INSTALMENT_AUD)}`
              : primaryLabel;

  // ---- Shared building blocks (composed differently per mode) ---------------

  const pieceNameInput = (
    <div>
      <label htmlFor="deposit-piece-name" className="label-small-caps mb-1.5 block">
        Piece name - optional
      </label>
      <input
        id="deposit-piece-name"
        type="text"
        value={pieceName}
        onChange={(e) => setPieceName(e.target.value)}
        data-testid="deposit-piece-name"
        placeholder="e.g. Jesus back piece"
        className="w-full rounded-md border border-gold-dim/30 bg-ink-deep px-3 py-2 font-serif text-sm text-cream placeholder:text-cream-faint focus:border-gold-bright focus:outline-none"
      />
      <PreferEarlierTick checked={preferEarlier} onChange={setPreferEarlier} className="mt-3" />
    </div>
  );

  const tiersBlock = (
    <section>
      <p className="label-small-caps mb-2">Session length</p>
      <div className="grid grid-cols-3 gap-2">
        {SESSION_OPTIONS.map((opt) => {
          const active = session === opt.value;
          const recommended = opt.value === initialSession;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => changeSession(opt.value)}
              disabled={useCustomRate}
              data-testid={`deposit-session-${opt.value}`}
              className={clsx(
                'relative rounded-lg border px-3 py-3 text-left transition disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none',
                active
                  ? 'border-gold-bright bg-gold/10'
                  : 'border-gold-dim/30 hover:border-gold-bright/60',
              )}
            >
              {recommended ? (
                <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[8px] font-inter uppercase tracking-widest bg-mint/30 text-mint">
                  <Sparkles size={8} /> Rec
                </span>
              ) : null}
              <p className="font-inter text-base text-cream">{opt.label}</p>
              <p className="font-mono text-[9px] uppercase tracking-widest text-cream-faint mt-0.5">
                {durationLabel(tierDurationMin(opt.value))}
              </p>
              <p className="font-cinzel text-lg text-gold-bright mt-0.5">
                {aud(depositForSession(opt.value, selectedArtist))}
              </p>
            </button>
          );
        })}
      </div>
    </section>
  );

  const customBlock = (
    <section data-testid="deposit-custom-rate">
      <p className="label-small-caps mb-2">Custom rate / deposit</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="deposit-day-rate" className="font-inter text-[10px] uppercase tracking-widest text-cream-faint block mb-1">
            Day rate (AUD)
          </label>
          <input
            id="deposit-day-rate"
            type="number"
            min={0}
            value={dayRateInput}
            onChange={(e) => setDayRateInput(e.target.value)}
            placeholder={String(autoDayRate)}
            data-testid="deposit-day-rate"
            className="w-full rounded-md border border-gold-dim/30 bg-ink-deep px-3 py-2 font-serif text-sm text-cream placeholder:text-cream-faint focus:border-gold-bright focus:outline-none"
          />
          <p className="font-mono text-[10px] uppercase tracking-widest text-cream-faint mt-1">
            {spec.label} rate card = {aud(autoDayRate)} · editable
          </p>
        </div>
        <div>
          <label htmlFor="deposit-amount" className="font-inter text-[10px] uppercase tracking-widest text-cream-faint block mb-1">
            Deposit (AUD)
          </label>
          <input
            id="deposit-amount"
            type="number"
            min={0}
            value={depositInput}
            onChange={(e) => setDepositInput(e.target.value)}
            placeholder={String(standardDeposit)}
            data-testid="deposit-amount"
            className="w-full rounded-md border border-gold-dim/30 bg-ink-deep px-3 py-2 font-serif text-sm text-cream placeholder:text-cream-faint focus:border-gold-bright focus:outline-none"
          />
          <p className="font-mono text-[10px] uppercase tracking-widest text-cream-faint mt-1">
            Standard {aud(standardDeposit)} · $0 = no deposit
          </p>
        </div>
      </div>
    </section>
  );

  // Toggle header gating the custom rate/deposit block. Off by default so the
  // tier presets stay the primary control; switching it on reveals the inputs
  // and greys out the presets (handled on each preset button above). Switching
  // it back off clears the overrides so the effective deposit/day-rate cleanly
  // reverts to the tier preset rather than leaving a hidden custom value live.
  const customRateSection = (
    <section className="rounded-lg border border-gold-dim/30 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="label-small-caps">Custom rate</p>
          <p className="font-serif text-[11px] text-cream-soft mt-0.5">
            Set your own day rate + deposit instead of the tier presets.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={useCustomRate}
          aria-label="Custom rate"
          onClick={() => {
            const next = !useCustomRate;
            setUseCustomRate(next);
            if (!next) {
              setDayRateInput('');
              setDepositInput('');
            }
          }}
          data-testid="deposit-custom-toggle"
          className={clsx(
            'relative h-6 w-11 shrink-0 rounded-full border transition',
            useCustomRate ? 'bg-gold/30 border-gold-bright' : 'bg-ink-deep border-gold-dim/40',
          )}
        >
          <span
            className={clsx(
              'absolute top-0.5 h-4 w-4 rounded-full transition-all',
              useCustomRate ? 'left-6 bg-gold-bright' : 'left-0.5 bg-cream-faint',
            )}
          />
        </button>
      </div>
      {useCustomRate ? customBlock : null}
    </section>
  );

  // Pay-over-time plan builder, revealed by the toggle. Sits directly under the
  // custom rate / deposit section in both layouts. Hidden for the Messenger
  // persona — they send simple dated deposit links, not top-up plans (tv-6qv.42).
  // bgg4 #6: a $0 deposit has nothing to split — never offer instalments for it.
  const topupBlock = persona === 'messenger' || effectiveDeposit <= 0 ? null : !planOffered ? (
    <section data-testid="deposit-topup" className="rounded-lg border border-gold-dim/30 p-3">
      <p className="label-small-caps">Set-up Top up deposits</p>
      <p className="font-serif text-[11px] text-cream-soft mt-0.5" data-testid="deposit-topup-method-note">
        Instalments go out as a deposit link. Choose &ldquo;Send deposit link&rdquo; to split the deposit.
      </p>
    </section>
  ) : (
    <section data-testid="deposit-topup" className="rounded-lg border border-gold-dim/30 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="label-small-caps">Set-up Top up deposits</p>
          <p className="font-serif text-[11px] text-cream-soft mt-0.5">
            Split the {aud(effectiveDeposit)} deposit into instalments — the first ($50 min) holds
            the date.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={planEnabled}
          aria-label="Set-up Top up deposits"
          onClick={() => setPlanEnabled((v) => !v)}
          data-testid="deposit-topup-toggle"
          className={clsx(
            'relative h-6 w-11 shrink-0 rounded-full border transition',
            planEnabled ? 'bg-gold/30 border-gold-bright' : 'bg-ink-deep border-gold-dim/40',
          )}
        >
          <span
            className={clsx(
              'absolute top-0.5 h-4 w-4 rounded-full transition-all',
              planEnabled ? 'left-6 bg-gold-bright' : 'left-0.5 bg-cream-faint',
            )}
          />
        </button>
      </div>

      {planEnabled ? (
        <div className="mt-3 space-y-3" data-testid="deposit-topup-builder">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="deposit-topup-start"
                className="font-inter text-[10px] uppercase tracking-widest text-cream-faint block mb-1"
              >
                First instalment
              </label>
              <input
                id="deposit-topup-start"
                type="date"
                value={planStartIso}
                onChange={(e) => setPlanStartIso(e.target.value)}
                data-testid="deposit-topup-start"
                className="w-full rounded-md border border-gold-dim/30 bg-ink-deep px-3 py-2 font-serif text-sm text-cream focus:border-gold-bright focus:outline-none"
              />
            </div>
            <div>
              <label
                htmlFor="deposit-topup-amount"
                className="font-inter text-[10px] uppercase tracking-widest text-cream-faint block mb-1"
              >
                Per instalment (AUD)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-serif text-sm text-cream-faint">
                  $
                </span>
                <input
                  id="deposit-topup-amount"
                  type="number"
                  min={MIN_INSTALMENT_AUD}
                  value={planInstalmentAud}
                  onChange={(e) => {
                    setPlanTouched(true);
                    setPlanInstalmentAud(Math.max(0, Math.round(Number(e.target.value) || 0)));
                  }}
                  data-testid="deposit-topup-amount"
                  className="w-full rounded-md border border-gold-dim/30 bg-ink-deep pl-6 pr-3 py-2 font-serif text-sm text-cream focus:border-gold-bright focus:outline-none"
                />
              </div>
            </div>
          </div>

          <div>
            <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint mb-1">
              Cadence
            </p>
            <div className="inline-flex overflow-hidden rounded-md border border-gold-dim/30">
              {(['weekly', 'fortnightly'] as TopupFrequency[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setPlanFrequency(f)}
                  data-testid={`deposit-topup-freq-${f}`}
                  className={clsx(
                    'px-3 py-1.5 font-inter text-xs capitalize transition',
                    planFrequency === f
                      ? 'bg-gold/10 text-gold-bright'
                      : 'text-cream-soft hover:text-cream',
                  )}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {planInvalid ? (
            <p className="font-serif text-[12px] text-blood" data-testid="deposit-topup-invalid">
              Each instalment must be at least {aud(MIN_INSTALMENT_AUD)}.
            </p>
          ) : (
            <div data-testid="deposit-topup-schedule">
              <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint mb-1.5">
                Schedule · {planSchedule.length} instalments
              </p>
              <ul className="space-y-1">
                {planSchedule.map((inst, i) => (
                  <li
                    key={inst.dueIso}
                    data-testid={`deposit-topup-instalment-${i}`}
                    className="flex items-center justify-between font-serif text-[13px] text-cream"
                  >
                    <span>
                      {format(new Date(inst.dueIso), 'EEE d MMM')}
                      {i === 0 ? (
                        <span className="ml-2 font-inter text-[9px] uppercase tracking-widest text-gold-bright">
                          holds the date
                        </span>
                      ) : null}
                    </span>
                    <span className="tabular-nums">{aud(inst.amountAud)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {planOverruns ? (
            <p
              className="font-serif text-[12px] text-orange flex items-start gap-1"
              data-testid="deposit-topup-overrun"
            >
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              Last instalment {format(new Date(planSchedule[planSchedule.length - 1]!.dueIso), 'd MMM')}{' '}
              — after the {format(new Date(appointmentIso), 'd MMM')} appointment. Every instalment has to be paid
              by then: raise the amount or shorten the plan to send it.
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );

  const channelBlock = (
    <section>
      <p className="label-small-caps mb-2">Send via</p>
      <div className="flex flex-wrap gap-2">
        {CHANNEL_OPTIONS.map((opt) => {
          const active = channel === opt.value;
          const recommended = opt.value === recommendedChannel;
          const Icon = opt.Icon;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setChannel(opt.value)}
              data-testid={`deposit-channel-${opt.value}`}
              className={clsx(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs transition',
                active
                  ? 'border-gold-bright bg-gold/10 text-gold-bright'
                  : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
              )}
            >
              <Icon size={12} /> {opt.label}
              {recommended ? (
                <span className="ml-1 px-1 py-0.5 rounded-sm text-[8px] font-inter uppercase tracking-widest bg-mint/30 text-mint">
                  Rec
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </section>
  );

  // L5 (q44j #4): a $0 deposit is never sent as a link (it needs a no-deposit
  // approval, or for a Messenger a push to the Agent), so there is no link text
  // to preview — it used to read "here's your deposit link …: $0".
  const previewBlock = needsApproval ? null : (
    <section className="panel-dark p-3">
      <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
        Preview ({channelMeta.label})
      </p>
      {planOn && !planInvalid ? (
        <p className="font-serif text-sm text-cream mt-2 leading-relaxed" data-testid="deposit-preview-plan">
          Hey {customer.firstName}, here&apos;s your deposit plan for the {spec.label.toLowerCase()}{' '}
          session: {scheduleLabel(planSchedule)}, {planFrequency}{' '}
          from {format(new Date(planStartIsoFull), 'EEE d MMM')}. The first payment holds your date.
        </p>
      ) : (
        <p className="font-serif text-sm text-cream mt-2 leading-relaxed">
          {linkMessage(mintedDepositId, aud).split('\n')[0]}
          <span className="block break-all text-cream-soft" data-testid="deposit-preview-link">
            {customerDepositUrl(mintedDepositId)}
          </span>
        </p>
      )}
      {routeNote ? (
        <p
          className="font-inter text-xs text-cream-soft mt-2"
          data-testid={linkRoute === 'instagram' ? 'deposit-route-instagram' : 'deposit-window-closed'}
        >
          {routeNote}
        </p>
      ) : null}
    </section>
  );

  // Booking-details confirmation card (pre-filled mode). Read-only summary of
  // the brief + slot, with the piece name still editable.
  const bookingDetailsBlock = (
    <section
      className="rounded-lg border border-gold-dim/30 p-3 space-y-3"
      data-testid="deposit-discussed-slot"
    >
      <p className="font-inter text-[10px] uppercase tracking-widest text-gold-bright">
        Booking details · from the brief - not shown to the customer
      </p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
        <div className="col-span-2">
          <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">Piece</dt>
          <dd className="font-serif text-sm text-cream">{pieceSummary ?? '—'}</dd>
        </div>
        <div>
          <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">Artist</dt>
          <dd className="font-serif text-sm text-cream inline-flex items-center gap-1">
            <Lock size={10} className="text-cream-faint" /> {selectedArtistName}
          </dd>
        </div>
        <div>
          <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">Date</dt>
          <dd className="font-serif text-sm text-cream">
            {startIso ? format(new Date(startIso), 'EEE d MMM') : ''}
          </dd>
        </div>
        {collabNames.length ? (
          <div className="col-span-2">
            <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">
              Collab
            </dt>
            <dd className="font-serif text-sm text-cream">{collabNames.join(', ')}</dd>
          </div>
        ) : null}
        <div>
          <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">Style</dt>
          <dd className="font-serif text-sm text-cream capitalize">{styleLabel ?? '—'}</dd>
        </div>
        <div>
          <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">Size</dt>
          <dd className="font-serif text-sm text-cream">{sizeLabel ?? '—'}</dd>
        </div>
        <div>
          <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">
            Placement
          </dt>
          <dd className="font-serif text-sm text-cream">
            <span className="capitalize">{placementLabel || '—'}</span>
            {/* Legacy records can carry both a structured part and a free-text
                note; keep the note visible rather than letting the formatter
                swallow it. */}
            {bodyPart && placement ? (
              <span className="block text-[12px] text-cream-soft">{placement}</span>
            ) : null}
          </dd>
        </div>
        <div>
          <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">Colour</dt>
          <dd className="font-serif text-sm text-cream">
            {colour === 'colour' ? 'Colour' : colour === 'black-and-grey' ? 'Black & grey' : '—'}
          </dd>
        </div>
        {di?.notes ? (
          <div className="col-span-2">
            <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">
              Notes
            </dt>
            <dd className="font-serif text-sm text-cream">{di.notes}</dd>
          </div>
        ) : null}
        {di?.notes ? (
          <div className="col-span-2">
            <dt className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">Notes</dt>
            <dd className="font-serif text-[13px] text-cream-soft">{di.notes}</dd>
          </div>
        ) : null}
      </dl>
      {briefRefs.length ? (
        <div data-testid="deposit-brief-refs">
          <p className="font-inter text-[9px] uppercase tracking-widest text-cream-faint mb-1.5">
            References · {briefRefs.length}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {briefRefs.slice(0, 6).map((url) => (
              <div
                key={url}
                className="h-10 w-10 overflow-hidden rounded-md border border-gold-dim/30"
              >
                <img src={url} alt="" className="h-full w-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {pieceNameInput}
      <p className="font-serif text-[11px] text-cream-soft">
        Confirm before sending - edit on the brief. The full brief rides onto the held booking.
      </p>
    </section>
  );

  // Session window: start locked to the slot; end + length adjustable and kept
  // in sync (length just steps the end), clamped to the free block.
  const sessionWindowBlock =
    startIso && blockEndIso ? (
      <section
        className="rounded-lg border border-gold-dim/30 p-3"
        data-testid="deposit-session-window"
      >
        <p className="label-small-caps mb-3">Session window</p>
        <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
          <div>
            <p className="font-inter text-[9px] uppercase tracking-widest text-cream-faint mb-1 inline-flex items-center gap-1">
              <Lock size={9} /> Start
            </p>
            <p className="font-serif text-sm text-cream" data-testid="deposit-start">
              {simTimeShort(startIso)}
            </p>
          </div>
          <div>
            <p className="font-inter text-[9px] uppercase tracking-widest text-cream-faint mb-1">End</p>
            <div className="inline-flex items-center gap-2">
              <button
                type="button"
                onClick={() => stepEnd(-15)}
                disabled={!canDecEnd}
                aria-label="End 15 minutes earlier"
                data-testid="deposit-end-minus"
                className="btn-quiet disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Minus size={12} />
              </button>
              <span
                className="font-serif text-sm text-cream min-w-[4.5rem] text-center"
                data-testid="deposit-end"
              >
                {effectiveEndIso ? simTimeShort(effectiveEndIso) : ''}
              </span>
              <button
                type="button"
                onClick={() => stepEnd(15)}
                disabled={!canIncEnd}
                aria-label="End 15 minutes later"
                data-testid="deposit-end-plus"
                className="btn-quiet disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus size={12} />
              </button>
            </div>
          </div>
          <div>
            <p className="font-inter text-[9px] uppercase tracking-widest text-cream-faint mb-1">Length</p>
            <div className="inline-flex items-center gap-2">
              <button
                type="button"
                onClick={() => stepEnd(-15)}
                disabled={!canDecEnd}
                aria-label="15 minutes shorter"
                data-testid="deposit-length-minus"
                className="btn-quiet disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Minus size={12} />
              </button>
              <span
                className="font-serif text-sm text-cream min-w-[4.5rem] text-center"
                data-testid="deposit-length"
              >
                {durationLabel(lengthMin)}
              </span>
              <button
                type="button"
                onClick={() => stepEnd(15)}
                disabled={!canIncEnd}
                aria-label="15 minutes longer"
                data-testid="deposit-length-plus"
                className="btn-quiet disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus size={12} />
              </button>
            </div>
          </div>
        </div>
        {isOvershoot ? (
          <p className="mt-2 font-serif text-[12px] text-blood" data-testid="deposit-overshoot">
            A {spec.label.toLowerCase()} session ({tierMin} min) runs past this window. Shorten the
            tier or pick a longer slot.
          </p>
        ) : null}
        {hasSlot && !isOvershoot ? (
          <p className="mt-2 font-serif text-[11px] text-cream-soft" data-testid="deposit-expiry">
            Hold expires in {expiryHours}h once the link is sent.
          </p>
        ) : null}
        <DoubleDayWarning
          artistId={artistId}
          proposed={hasSlot && startIso ? { start: startIso, durationMin: lengthMin, sessionLength: session } : null}
          testId="deposit-double-day-warning"
        />
      </section>
    ) : null;

  const fromScratchPicker = (
    <section data-testid="deposit-slot-picker">
      <p className="label-small-caps mb-2">Artist + date</p>
      <div className="flex flex-wrap gap-2">
        <select
          aria-label="Artist"
          value={artistId}
          onChange={(e) => {
            setArtistId(e.target.value);
            setBlockStartIso(null);
            setBlockEndIso(null);
            setEndIso(null);
          }}
          data-testid="deposit-artist-select"
          className="flex-1 min-w-[10rem] rounded-md border border-gold-dim/30 bg-ink-deep px-3 py-2 font-serif text-sm text-cream focus:border-gold-bright focus:outline-none"
        >
          {studioArtists.length === 0 ? (
            <option value="">No artists at this studio</option>
          ) : (
            studioArtists.map((a) => (
              <option key={a.id} value={a.id}>
                {a.displayName}
              </option>
            ))
          )}
        </select>
        <input
          type="date"
          aria-label="Date"
          value={dateValue}
          onChange={(e) => {
            setDateValue(e.target.value);
            setBlockStartIso(null);
            setBlockEndIso(null);
            setEndIso(null);
          }}
          data-testid="deposit-date"
          className="rounded-md border border-gold-dim/30 bg-ink-deep px-3 py-2 font-serif text-sm text-cream focus:border-gold-bright focus:outline-none"
        />
      </div>
      <div className="mt-3">
        <ArtistDaySchedule artistId={artistId} dayIso={dayIso} onPickWindow={onPickWindow} />
      </div>
      {!startIso ? (
        <p className="font-serif text-[11px] text-cream-soft" data-testid="deposit-pick-prompt">
          Pick a free window above to ground the deposit.
        </p>
      ) : null}
    </section>
  );

  // No-deposit block, shown only when the deposit is $0. A reason is mandatory
  // (approvals refuses a blank note); privileged personas waive directly, the
  // rest raise a request. Mirrors NewBookingModal's no-deposit surface.
  // B8 (75mx, diagram 03): Messengers can't do no-deposit bookings — that override
  // is Agent-requested, Ops-approved. They push the lead instead.
  const messengerNoDeposit = needsApproval && persona === 'messenger';
  const noDepositBlock = messengerNoDeposit ? (
    <section
      className="rounded-md border border-orange/40 bg-orange/5 p-3"
      data-testid="deposit-messenger-no-deposit"
    >
      <p className="flex items-start gap-1.5 text-[12px] text-orange">
        <AlertTriangle size={13} className="mt-px shrink-0" />
        Messengers can&rsquo;t book without a deposit. Push the lead to the Agent instead.
      </p>
    </section>
  ) : needsApproval ? (
    <section
      className="rounded-md border border-orange/40 bg-orange/5 p-3"
      data-testid="deposit-no-deposit-block"
    >
      <p className="flex items-start gap-1.5 text-[12px] text-orange">
        <AlertTriangle size={13} className="mt-px shrink-0" />
        {declinedNoDeposit
          ? 'Ops declined a no-deposit booking for this customer. Collect a deposit instead.'
          : isPrivileged
            ? 'No deposit on this booking. Waiving it needs a reason - it will be logged against the booking.'
            : "No deposit on this booking. It can't be sent directly - send it to Georgina for no-deposit approval."}
      </p>
      <label className="mt-2 block">
        <span className="label-small-caps">
          No-deposit reason <span className="text-blood">*</span>
        </span>
        <textarea
          value={overrideReason}
          onChange={(e) => setOverrideReason(e.target.value)}
          rows={2}
          placeholder="e.g. trusted repeat customer, paying full on the day"
          data-testid="deposit-no-deposit-reason"
          className="mt-2 w-full rounded-md border border-gold-dim/30 bg-ink-deep px-3 py-2 font-serif text-sm text-cream placeholder:text-cream-faint focus:border-gold-bright focus:outline-none"
        />
      </label>
    </section>
  ) : null;

  // Payment method (Diagram 02 / tv-6qv.13), shown for a positive deposit to any
  // persona that can take payment. Messengers are link-only — the whole chooser
  // is theirs-hidden and the footer simply sends the link. Name/phone are no
  // longer collected here: an agent takes payment on a call with the number
  // already on file, and the messenger's customer supplies it customer-side when
  // they pay the link (it's saved to their profile then).
  const paymentContactBlock = !needsApproval && canTakePayment ? (
    <section
      className="rounded-lg border border-gold-dim/30 p-3 space-y-2"
      data-testid="deposit-payment-contact"
    >
      <p className="label-small-caps mb-1.5">How are they paying?</p>
      <p className="font-serif text-[11px] text-cream-soft mb-2">
        {inShop
          ? 'Take card or cash in the shop now, or send them a deposit link.'
          : 'Send a link, take a card over the phone now, or hold the slot to pay at the studio.'}
      </p>
      <div className="flex flex-wrap gap-2">
        {payMethodOptions.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => {
              setPayMethod(m.key);
              setIntentError(null);
            }}
            data-testid={`deposit-paymethod-${m.key}`}
            className={clsx(
              'inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs transition',
              payMethod === m.key
                ? 'border-gold-bright bg-gold/10 text-gold-bright'
                : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
            )}
          >
            <m.Icon size={12} /> {m.label}
          </button>
        ))}
      </div>
      {isTakeNow && onFilePhone.length === 0 ? (
        <p className="font-serif text-[11px] text-blood" data-testid="deposit-no-phone-note">
          No phone on file. Add one with Edit details on their record before taking payment. The receipt is sent there.
        </p>
      ) : null}
      {isTakeNow ? <DepositEmailField customer={customer} value={takeEmail} onChange={setTakeEmail} className="pt-1" /> : null}
    </section>
  ) : null;

  const sendFooter = (
    <footer className="flex flex-wrap justify-end gap-2 mt-5">
      {intentError && isTakeNow ? (
        <p
          role="alert"
          className="basis-full font-serif text-[11px] text-blood text-right"
          data-testid="deposit-intent-error"
        >
          <span className="font-semibold">{intentError.title}.</span> {intentError.body}
        </p>
      ) : null}
      <button
        type="button"
        onClick={onClose}
        className="btn-quiet text-xs"
        data-testid="deposit-modal-cancel"
      >
        Cancel
      </button>
      {messengerNoDeposit ? null : needsApproval ? (
        isPrivileged ? (
          <button
            type="button"
            onClick={submitNoDeposit}
            disabled={!canSubmitNoDeposit}
            className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="deposit-waive-no-deposit"
            title={
              canSubmitNoDeposit ? 'Confirm with no deposit' : 'Add a reason to waive the deposit'
            }
          >
            Confirm - no deposit
          </button>
        ) : (
          <button
            type="button"
            onClick={submitNoDeposit}
            disabled={!canSubmitNoDeposit}
            className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="deposit-request-no-deposit"
            title={
              canSubmitNoDeposit
                ? 'Send for no-deposit approval'
                : 'Add a reason to request approval'
            }
          >
            Request no-deposit approval
          </button>
        )
      ) : (
        <button
          type="button"
          onClick={onPrimary}
          disabled={primaryDisabled}
          className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="deposit-modal-send"
          title={primaryTitle}
        >
          <PrimaryIcon size={12} /> {primaryLabel}
        </button>
      )}
    </footer>
  );

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-overlay/70 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={onClose}
          data-testid="deposit-link-modal"
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            className={clsx(
              'panel bg-ink-deep px-6 py-5 w-full max-h-[90vh] overflow-y-auto shadow-gold-glow',
              // Both modes are wide now: from-scratch used to be a narrow max-w-xl
              // single column with a tall scroll (tv-6qv.32).
              'max-w-4xl',
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-start justify-between mb-4">
              <div>
                <p className="label-small-caps text-gold-bright" data-testid="deposit-modal-heading">
                  {heading ?? 'Send deposit link'}
                </p>
                <h2 className="heading-display text-2xl text-cream mt-1">
                  {customerName(customer)}
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="btn-quiet"
                aria-label="Close"
                data-testid="deposit-modal-close"
              >
                <X size={14} />
              </button>
            </header>

            {grounded ? (
              // Pre-filled: wide two columns - confirm + when | deposit + send.
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-4">
                  {bookingDetailsBlock}
                  {sessionWindowBlock}
                </div>
                <div className="space-y-5">
                  {tiersBlock}
                  {customRateSection}
                  {topupBlock}
                  {channelBlock}
                  {previewBlock}
                </div>
              </div>
            ) : (
              // From scratch: wide two columns (tv-6qv.32) — pick a slot | set the
              // deposit. Mirrors the grounded layout so from-scratch no longer runs
              // as one tall single-column scroll.
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-4">
                  {pieceNameInput}
                  {fromScratchPicker}
                  {sessionWindowBlock}
                </div>
                <div className="space-y-5">
                  {tiersBlock}
                  {customRateSection}
                  {topupBlock}
                  {channelBlock}
                  {hasSlot ? previewBlock : null}
                </div>
              </div>
            )}

            {noDepositBlock ? <div className="mt-5">{noDepositBlock}</div> : null}
            {paymentContactBlock ? <div className="mt-5">{paymentContactBlock}</div> : null}
            {sendFooter}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
